import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leadsApi, aiApi, proposalsApi, dealsApi, leadsTimelineApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useProduct } from '../context/ProductContext';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  Calendar,
  Zap,
  FileText,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Send,
  Upload,
  Edit,
  Pause,
  Play,
  Ban,
  Activity,
  Bot,
  Sparkles
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';

const STAGES = {
  new: { label: 'New', color: 'bg-slate-100 text-slate-700', icon: Clock },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-800', icon: Mail },
  booked: { label: 'Booked', color: 'bg-purple-100 text-purple-800', icon: Calendar },
  discovery_done: { label: 'Discovery Done', color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle },
  proposal_draft: { label: 'Proposal Draft', color: 'bg-amber-100 text-amber-800', icon: FileText },
  proposal_sent: { label: 'Proposal Sent', color: 'bg-orange-100 text-orange-800', icon: Send },
  closed_won: { label: 'Won', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  closed_lost: { label: 'Lost', color: 'bg-red-100 text-red-800', icon: XCircle }
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [lead, setLead] = useState(null);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(null);
  
  // Modals
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [showCloseDealModal, setShowCloseDealModal] = useState(false);
  const [closeDealType, setCloseDealType] = useState(null);
  const [showEditLead, setShowEditLead] = useState(false);

  useEffect(() => {
    loadLeadData();
  }, [id]);

  const loadLeadData = async () => {
    try {
      setLoading(true);
      const [leadRes, commsRes] = await Promise.all([
        leadsApi.get(id),
        leadsApi.getCommunications(id)
      ]);
      setLead(leadRes.data);
      setCommunications(commsRes.data);
    } catch (error) {
      toast.error('Failed to load lead data');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const handleAIAction = async (action) => {
    setAiLoading(action);
    try {
      switch (action) {
        case 'schedule':
          await aiApi.triggerScheduling(id);
          toast.success('Scheduling message sent');
          break;
        case 'follow_up_confirmation':
          await aiApi.sendFollowUp(id, 'booking_confirmation');
          toast.success('Confirmation sent');
          break;
        case 'follow_up_reminder':
          await aiApi.sendFollowUp(id, 'reminder_24h');
          toast.success('Reminder sent');
          break;
        case 'follow_up_proposal':
          await aiApi.sendFollowUp(id, 'proposal_follow_up');
          toast.success('Follow-up sent');
          break;
        case 'generate_proposal':
          await proposalsApi.generate(id);
          toast.success('Proposal generated');
          break;
        default:
          break;
      }
      loadLeadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setAiLoading(null);
    }
  };

  const handleAnalyzeTranscript = async (transcript) => {
    setAiLoading('analyze');
    try {
      await aiApi.analyzeDiscovery(id, { lead_id: id, transcript });
      toast.success('Transcript analyzed');
      setShowTranscriptModal(false);
      loadLeadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAiLoading(null);
    }
  };

  const handleCloseDeal = async (data) => {
    try {
      if (closeDealType === 'won') {
        await dealsApi.markWon(id, data.dealValue, data.notes);
        toast.success('Deal marked as won!');
      } else {
        await dealsApi.markLost(id, data.lostReason, data.notes);
        toast.success('Deal marked as lost');
      }
      setShowCloseDealModal(false);
      loadLeadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close deal');
    }
  };

  const handlePauseAutomation = async () => {
    try {
      if (lead.automation_paused) {
        await leadsApi.resumeAutomation(id);
        toast.success('Automation resumed');
      } else {
        await leadsApi.pauseAutomation(id);
        toast.success('Automation paused');
      }
      loadLeadData();
    } catch (error) {
      toast.error('Failed to update automation');
    }
  };

  const handleOptOut = async () => {
    if (!window.confirm('This will permanently stop all automation for this lead. Continue?')) {
      return;
    }
    try {
      await leadsApi.optOut(id);
      toast.success('Lead opted out');
      loadLeadData();
    } catch (error) {
      toast.error('Failed to opt out');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  const stageInfo = STAGES[lead.stage] || STAGES.new;
  const StageIcon = stageInfo.icon;

  return (
    <div className="p-8" data-testid="lead-detail-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/leads')}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4"
          data-testid="back-to-leads"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Leads
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{lead.name}</h1>
            <div className="flex items-center gap-4 mt-2 text-slate-500 flex-wrap">
              <span className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {lead.company}
              </span>
              <span className={`status-badge ${stageInfo.color}`}>
                <StageIcon className="w-3 h-3 mr-1" />
                {stageInfo.label}
              </span>
              {lead.product_name && (
                <span
                  className="status-badge bg-indigo-50 text-indigo-700 border border-indigo-200"
                  data-testid="lead-product-badge"
                >
                  {lead.product_name}
                </span>
              )}
              {!lead.product_id && (
                <span className="status-badge bg-slate-100 text-slate-500 border border-slate-200" data-testid="lead-unassigned-badge">
                  Unassigned
                </span>
              )}
              {lead.opt_out && (
                <span className="status-badge status-badge-error">
                  <Ban className="w-3 h-3 mr-1" />
                  Opted Out
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowEditLead(true)}
              data-testid="edit-lead-btn"
            >
              <Edit className="w-4 h-4" />
              Edit
            </Button>
            {lead.stage === 'proposal_sent' && (
              <>
                <Button
                  className="btn-primary gap-2"
                  onClick={() => { setCloseDealType('won'); setShowCloseDealModal(true); }}
                  data-testid="mark-won-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark Won
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => { setCloseDealType('lost'); setShowCloseDealModal(true); }}
                  data-testid="mark-lost-btn"
                >
                  <XCircle className="w-4 h-4" />
                  Mark Lost
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium">{lead.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="font-medium">{lead.phone || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs for Activity & Insights */}
          <Tabs defaultValue="timeline" className="space-y-4">
            <TabsList>
              <TabsTrigger value="timeline" data-testid="tab-timeline">Journey Timeline</TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
              <TabsTrigger value="insights" data-testid="tab-insights">Discovery Insights</TabsTrigger>
            </TabsList>

            <TabsContent value="timeline">
              <JourneyTimeline leadId={id} />
            </TabsContent>

            <TabsContent value="activity">
              <Card>
                <CardHeader>
                  <CardTitle>Communication Log</CardTitle>
                </CardHeader>
                <CardContent>
                  {communications.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No communications yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {communications.map((comm, i) => (
                        <div 
                          key={i}
                          className="flex gap-4 p-4 bg-slate-50 rounded-lg"
                        >
                          <div className={`p-2 rounded-full ${
                            comm.type === 'email' ? 'bg-blue-100' :
                            comm.type === 'ai_message' ? 'bg-purple-100' :
                            'bg-slate-200'
                          }`}>
                            {comm.type === 'email' ? (
                              <Mail className="w-4 h-4 text-blue-600" />
                            ) : (
                              <Zap className="w-4 h-4 text-purple-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-sm">
                                {comm.subject || comm.type.replace('_', ' ').toUpperCase()}
                              </p>
                              <span className="text-xs text-slate-400">
                                {new Date(comm.sent_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1">
                              {comm.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="insights">
              <Card>
                <CardHeader>
                  <CardTitle>Discovery Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  {lead.discovery_insights ? (
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium mb-2">Summary</h4>
                        <p className="text-slate-600">{lead.discovery_insights.summary}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium mb-2">Pain Points</h4>
                          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                            {lead.discovery_insights.pain_points?.map((p, i) => (
                              <li key={i}>{p}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Risks</h4>
                          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                            {lead.discovery_insights.risks?.map((r, i) => (
                              <li key={i}>{r}</li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Deal Score</span>
                          <span className="text-2xl font-bold text-blue-600">
                            {lead.discovery_insights.deal_score}/10
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-500 mb-4">No discovery insights yet</p>
                      <Button
                        variant="outline"
                        onClick={() => setShowTranscriptModal(true)}
                        data-testid="upload-transcript-btn"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Call Transcript
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar - Actions */}
        <div className="space-y-6">
          {/* AI Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-blue-600" />
                AI Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {lead.consent_status !== 'verified' && (
                <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Consent not verified. AI actions disabled.
                </div>
              )}

              {lead.opt_out && (
                <div className="p-3 bg-red-50 rounded-lg text-sm text-red-800">
                  <Ban className="w-4 h-4 inline mr-2" />
                  Lead opted out. All automation disabled.
                </div>
              )}

              {!lead.opt_out && lead.consent_status === 'verified' && (
                <>
                  {['new', 'contacted'].includes(lead.stage) && (
                    <Button
                      className="w-full justify-start gap-2"
                      variant="outline"
                      onClick={() => handleAIAction('schedule')}
                      disabled={aiLoading || lead.nudge_count >= 2}
                      data-testid="ai-schedule-btn"
                    >
                      <Mail className="w-4 h-4" />
                      {aiLoading === 'schedule' ? 'Sending...' : 'Send Booking Message'}
                      {lead.nudge_count > 0 && (
                        <span className="ml-auto text-xs text-slate-400">
                          ({lead.nudge_count}/2 sent)
                        </span>
                      )}
                    </Button>
                  )}

                  {lead.stage === 'booked' && (
                    <>
                      <Button
                        className="w-full justify-start gap-2"
                        variant="outline"
                        onClick={() => handleAIAction('follow_up_confirmation')}
                        disabled={aiLoading}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Send Confirmation
                      </Button>
                      <Button
                        className="w-full justify-start gap-2"
                        variant="outline"
                        onClick={() => handleAIAction('follow_up_reminder')}
                        disabled={aiLoading}
                      >
                        <Clock className="w-4 h-4" />
                        Send Reminder
                      </Button>
                      <Button
                        className="w-full justify-start gap-2"
                        variant="outline"
                        onClick={() => setShowTranscriptModal(true)}
                        data-testid="upload-transcript-action"
                      >
                        <Upload className="w-4 h-4" />
                        Upload Transcript
                      </Button>
                    </>
                  )}

                  {lead.stage === 'discovery_done' && (
                    <Button
                      className="w-full justify-start gap-2 btn-primary"
                      onClick={() => handleAIAction('generate_proposal')}
                      disabled={aiLoading}
                      data-testid="generate-proposal-btn"
                    >
                      <FileText className="w-4 h-4" />
                      {aiLoading === 'generate_proposal' ? 'Generating...' : 'Generate Proposal'}
                    </Button>
                  )}

                  {lead.stage === 'proposal_sent' && (
                    <Button
                      className="w-full justify-start gap-2"
                      variant="outline"
                      onClick={() => handleAIAction('follow_up_proposal')}
                      disabled={aiLoading}
                    >
                      <Send className="w-4 h-4" />
                      Send Follow-up
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Automation Controls */}
          <Card>
            <CardHeader>
              <CardTitle>Automation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full justify-start gap-2"
                variant="outline"
                onClick={handlePauseAutomation}
                disabled={lead.opt_out}
              >
                {lead.automation_paused ? (
                  <>
                    <Play className="w-4 h-4" />
                    Resume Automation
                  </>
                ) : (
                  <>
                    <Pause className="w-4 h-4" />
                    Pause Automation
                  </>
                )}
              </Button>
              
              {!lead.opt_out && (
                <Button
                  className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  variant="ghost"
                  onClick={handleOptOut}
                  data-testid="opt-out-btn"
                >
                  <Ban className="w-4 h-4" />
                  Opt Out Lead
                </Button>
              )}
            </CardContent>
          </Card>

          {/* SMS & Call Actions */}
          {lead.phone && !lead.opt_out && (
            <SMSCard lead={lead} onSuccess={() => loadLeadData()} />
          )}

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 text-sm">
                {lead.notes || 'No notes added'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upload Transcript Modal */}
      <TranscriptModal
        open={showTranscriptModal}
        onClose={() => setShowTranscriptModal(false)}
        onSubmit={handleAnalyzeTranscript}
        loading={aiLoading === 'analyze'}
      />

      {/* Close Deal Modal */}
      <CloseDealModal
        open={showCloseDealModal}
        onClose={() => setShowCloseDealModal(false)}
        type={closeDealType}
        onSubmit={handleCloseDeal}
      />

      {/* Edit Lead Modal */}
      <EditLeadModal
        open={showEditLead}
        onClose={() => setShowEditLead(false)}
        lead={lead}
        onSaved={loadLeadData}
      />
    </div>
  );
}

// Transcript Modal
function TranscriptModal({ open, onClose, onSubmit, loading }) {
  const [transcript, setTranscript] = useState('');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload Discovery Call Transcript</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Label>Paste the call transcript below</Label>
          <Textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            rows={12}
            placeholder="Paste the full transcript of your discovery call here..."
            className="mt-2"
            data-testid="transcript-input"
          />
          <p className="text-xs text-slate-400 mt-2">
            AI will analyze the transcript to extract pain points, budget signals, and deal score.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className="btn-primary"
            onClick={() => onSubmit(transcript)}
            disabled={!transcript.trim() || loading}
            data-testid="analyze-transcript-btn"
          >
            {loading ? 'Analyzing...' : 'Analyze Transcript'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Close Deal Modal
function CloseDealModal({ open, onClose, type, onSubmit }) {
  const [dealValue, setDealValue] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = () => {
    if (type === 'won') {
      onSubmit({ dealValue: parseFloat(dealValue) || 0, notes });
    } else {
      onSubmit({ lostReason, notes });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {type === 'won' ? '🎉 Mark Deal as Won' : 'Mark Deal as Lost'}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {type === 'won' ? (
            <div>
              <Label>Deal Value ($)</Label>
              <Input
                type="number"
                value={dealValue}
                onChange={(e) => setDealValue(e.target.value)}
                placeholder="Enter deal value"
                data-testid="deal-value-input"
              />
            </div>
          ) : (
            <div>
              <Label>Lost Reason *</Label>
              <Select value={lostReason} onValueChange={setLostReason}>
                <SelectTrigger data-testid="lost-reason-select">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="budget">Budget constraints</SelectItem>
                  <SelectItem value="timing">Bad timing</SelectItem>
                  <SelectItem value="competitor">Went with competitor</SelectItem>
                  <SelectItem value="no_need">No longer needed</SelectItem>
                  <SelectItem value="no_response">No response</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add any notes..."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className={type === 'won' ? 'btn-primary' : ''}
            variant={type === 'lost' ? 'destructive' : 'default'}
            onClick={handleSubmit}
            disabled={type === 'lost' && !lostReason}
            data-testid="confirm-close-deal"
          >
            {type === 'won' ? 'Mark as Won' : 'Mark as Lost'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// SMS & Call Card Component
function SMSCard({ lead, onSuccess }) {
  const [smsMessage, setSmsMessage] = useState('');
  const [sending, setSending] = useState(null);
  const [showSmsModal, setShowSmsModal] = useState(false);
  
  // Import the API
  const { smsApi } = require('../services/api');

  const handleSendSms = async () => {
    if (!smsMessage.trim()) return;
    
    setSending('manual');
    try {
      await smsApi.sendToLead(lead.id, smsMessage);
      toast.success('SMS sent successfully');
      setSmsMessage('');
      setShowSmsModal(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSending(null);
    }
  };

  const handleSendAiSms = async (type) => {
    setSending(type);
    try {
      await smsApi.sendAiSms(lead.id, type);
      toast.success('AI SMS sent successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSending(null);
    }
  };

  const handleCall = async () => {
    setSending('call');
    try {
      await smsApi.callLead(lead.id);
      toast.success('Call initiated');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
    } finally {
      setSending(null);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-600" />
            SMS & Call
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-slate-500 mb-2">
            <Phone className="w-4 h-4 inline mr-1" />
            {lead.phone}
          </div>
          
          <Button
            className="w-full justify-start gap-2"
            variant="outline"
            onClick={() => setShowSmsModal(true)}
            disabled={sending}
            data-testid="send-sms-btn"
          >
            <MessageSquare className="w-4 h-4" />
            Send SMS
          </Button>

          <Button
            className="w-full justify-start gap-2"
            variant="outline"
            onClick={() => handleSendAiSms('follow_up')}
            disabled={sending}
            data-testid="ai-sms-btn"
          >
            <Zap className="w-4 h-4" />
            {sending === 'follow_up' ? 'Generating...' : 'AI Follow-up SMS (3 credits)'}
          </Button>

          <Button
            className="w-full justify-start gap-2"
            variant="outline"
            onClick={handleCall}
            disabled={sending}
            data-testid="call-lead-btn"
          >
            <Phone className="w-4 h-4" />
            {sending === 'call' ? 'Calling...' : 'Call Lead'}
          </Button>
        </CardContent>
      </Card>

      {/* SMS Compose Modal */}
      <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send SMS to {lead.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Message (max 160 characters)</Label>
            <Textarea
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value.slice(0, 160))}
              rows={4}
              placeholder="Type your SMS message..."
              className="mt-2"
              data-testid="sms-message-input"
            />
            <p className="text-xs text-slate-400 mt-2">
              {smsMessage.length}/160 characters
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSmsModal(false)}>Cancel</Button>
            <Button
              className="btn-primary gap-2"
              onClick={handleSendSms}
              disabled={!smsMessage.trim() || sending === 'manual'}
              data-testid="confirm-send-sms"
            >
              <Send className="w-4 h-4" />
              {sending === 'manual' ? 'Sending...' : 'Send SMS'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============ JOURNEY TIMELINE ============

const TIMELINE_KIND_STYLES = {
  action: { dot: 'bg-amber-500', icon: Bot, label: 'Action', tone: 'border-amber-200' },
  usage: { dot: 'bg-purple-500', icon: Sparkles, label: 'AI Usage', tone: 'border-purple-200' },
  communication: { dot: 'bg-blue-500', icon: Mail, label: 'Communication', tone: 'border-blue-200' }
};

function JourneyTimeline({ leadId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await leadsTimelineApi.timeline(leadId);
        setEvents(r.data?.events || []);
      } catch {
        toast.error('Failed to load timeline');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [leadId]);

  const filtered = kindFilter === 'all' ? events : events.filter((e) => e.kind === kindFilter);

  const formatTs = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  return (
    <Card data-testid="journey-timeline">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-indigo-600" />
            Journey Timeline
          </CardTitle>
          <div className="flex gap-1">
            {['all', 'action', 'usage', 'communication'].map((k) => (
              <Button
                key={k}
                size="sm"
                variant={kindFilter === k ? 'default' : 'outline'}
                onClick={() => setKindFilter(k)}
                className="capitalize"
                data-testid={`timeline-filter-${k}`}
              >
                {k}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">Loading timeline…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Activity className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p>No timeline events yet</p>
          </div>
        ) : (
          <ol className="relative border-l border-slate-200 ml-2 space-y-4">
            {filtered.map((e, i) => {
              const style = TIMELINE_KIND_STYLES[e.kind] || TIMELINE_KIND_STYLES.action;
              const IconC = style.icon;
              return (
                <li key={i} className="ml-4" data-testid={`timeline-event-${i}`}>
                  <span className={`absolute -left-1.5 flex h-3 w-3 items-center justify-center rounded-full ${style.dot}`} />
                  <div className={`p-3 rounded border ${style.tone} bg-white`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <IconC className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-medium capitalize">
                          {String(e.action || style.label).replace(/_/g, ' ')}
                        </span>
                        {e.agent_type && (
                          <Badge variant="outline" className="text-[10px] capitalize bg-purple-50 text-purple-700 border-purple-200">
                            {String(e.agent_type).replace(/_/g, ' ')}
                          </Badge>
                        )}
                        {e.status && (
                          <Badge variant="outline" className="text-[10px] capitalize">{e.status}</Badge>
                        )}
                        {e.trigger && (
                          <Badge variant="outline" className="text-[10px] capitalize bg-blue-50 text-blue-700 border-blue-200">
                            {String(e.trigger).replace(/_/g, ' ')}
                          </Badge>
                        )}
                      </div>
                      <span className="text-[11px] text-slate-400">{formatTs(e.timestamp)}</span>
                    </div>
                    {e.ai_reason && (
                      <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900">
                        <span className="font-medium">AI Reason:</span> {e.ai_reason}
                      </div>
                    )}
                    {e.subject && (
                      <div className="mt-2 text-xs"><span className="text-slate-500">Subject:</span> <span className="font-medium">{e.subject}</span></div>
                    )}
                    {e.preview && (
                      <div className="mt-1 text-xs text-slate-600 whitespace-pre-wrap line-clamp-3">{e.preview}</div>
                    )}
                    {(e.credits_used || e.credits_used === 0) && e.kind === 'usage' && (
                      <div className="mt-2 text-[11px] text-slate-500">
                        Credits: {Number(e.credits_used).toFixed(2)}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}



// ============ EDIT LEAD MODAL ============

function EditLeadModal({ open, onClose, lead, onSaved }) {
  const { products } = useProduct();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setForm({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        company: lead.company || '',
        product_id: lead.product_id || '',
        goal: lead.goal || '',
        pain_point: lead.pain_point || '',
        consent_status: lead.consent_status || 'pending',
        notes: lead.notes || '',
      });
    }
  }, [lead, open]);

  const save = async () => {
    setSaving(true);
    try {
      // product_id: empty string = explicit Unassigned; backend resolves to nulls.
      const payload = { ...form };
      await leadsApi.update(lead.id, payload);
      toast.success('Lead updated');
      onClose();
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-lead-dialog">
        <DialogHeader>
          <DialogTitle>Edit Lead</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input
                value={form.name || ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="edit-lead-name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={form.email || ''}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="edit-lead-email"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone || ''}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Company</Label>
              <Input
                value={form.company || ''}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Product / Service</Label>
            <Select
              value={form.product_id || 'none'}
              onValueChange={(v) => setForm({ ...form, product_id: v === 'none' ? '' : v })}
            >
              <SelectTrigger data-testid="edit-lead-product-select">
                <SelectValue placeholder="Select product (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Unassigned —</SelectItem>
                {(products || []).filter((p) => p.is_active).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Goal</Label>
              <Input
                value={form.goal || ''}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                placeholder="e.g. save_time"
              />
            </div>
            <div>
              <Label>Pain Point</Label>
              <Input
                value={form.pain_point || ''}
                onChange={(e) => setForm({ ...form, pain_point: e.target.value })}
                placeholder="e.g. manual_follow_up"
              />
            </div>
          </div>
          <div>
            <Label>Consent Status</Label>
            <Select
              value={form.consent_status || 'pending'}
              onValueChange={(v) => setForm({ ...form, consent_status: v })}
            >
              <SelectTrigger data-testid="edit-lead-consent-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-edit-lead-btn">
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
